/**
 * Admin Cameras Health Monitoring
 * Shows real-time camera status from Hik-Connect API
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, ChevronLeft, RefreshCw, AlertTriangle,
  CheckCircle2, Clock, XCircle, Wrench, Signal,
  Wifi, WifiOff, HardDrive, Server, Cpu, 
  Zap, ChevronDown, ChevronUp, Info, MapPin, Building2, Filter, X,
  Play, Video, Loader2, ExternalLink, Copy, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Info row component - moved outside to avoid re-creation during render
const InfoRow = ({ icon: Icon, label, value, valueClass = '' }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
    <span className="flex items-center gap-2 text-muted-foreground text-sm">
      <Icon className="w-4 h-4" />
      {label}
    </span>
    <span className={`text-sm font-medium ${valueClass}`}>{value || 'N/A'}</span>
  </div>
);

// Status config
const STATUS_CONFIG = {
  online: { 
    icon: CheckCircle2, 
    color: "text-green-400", 
    bgColor: "bg-green-500/20",
    label: "En línea" 
  },
  offline: { 
    icon: XCircle, 
    color: "text-red-400", 
    bgColor: "bg-red-500/20",
    label: "Fuera de línea" 
  },
  maintenance: { 
    icon: Wrench, 
    color: "text-yellow-400", 
    bgColor: "bg-yellow-500/20",
    label: "Mantenimiento" 
  },
  error: { 
    icon: AlertTriangle, 
    color: "text-red-400", 
    bgColor: "bg-red-500/20",
    label: "Error" 
  }
};

// Camera card component with detailed info and clip testing
const CameraCard = ({ camera }) => {
  const [expanded, setExpanded] = useState(false);
  const [testingClip, setTestingClip] = useState(null); // '30' or '60'
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState(''); // email, phone, or user_id to assign clip
  
  const status = STATUS_CONFIG[camera.status] || STATUS_CONFIG.offline;
  const StatusIcon = status.icon;
  
  // Check if camera is assigned to a court
  const isAssignedToCourt = camera.court_id && camera.court_id !== 'unknown';
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Desconocido';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };
  
  const formatLastSeen = (isoString) => {
    if (!isoString) return 'Nunca';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)} horas`;
    return formatDate(isoString);
  };
  
  // Handle clip test request
  const handleTestClip = async (duration) => {
    if (testingClip || !camera.online) return;
    
    setTestingClip(duration.toString());
    setTestResult(null);
    
    try {
      const requestData = {
        camera_id: camera.hikconnect_id,
        club_id: camera.club_id || 'unknown',
        court_id: camera.court_id || 'unknown',
        duration: duration
      };
      
      // Add user identifier if provided (to simulate clip delivery to a specific user)
      if (userIdentifier.trim()) {
        requestData.user_identifier = userIdentifier.trim();
      }
      
      const response = await adminAPI.testCamera(requestData);
      
      setTestResult(response.data);
      
      if (response.data.status === 'success') {
        const assignedMsg = response.data.assigned_to_user 
          ? ` y asignado a ${response.data.assigned_to_user.email || response.data.assigned_to_user.phone}`
          : '';
        toast.success(`¡Clip de ${duration}s generado correctamente${assignedMsg}!`);
      } else {
        toast.error(`Error: ${response.data.steps?.find(s => s.status === 'failed')?.error || 'Fallo desconocido'}`);
      }
    } catch (err) {
      console.error('Test clip error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al generar clip';
      setTestResult({ status: 'error', error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingClip(null);
    }
  };
  
  // Copy URL to clipboard with fallback for security restrictions
  const copyToClipboard = async (url) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success('URL copiada al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      // Ultimate fallback - show URL in a prompt for manual copy
      toast.info('No se pudo copiar automáticamente. Copia la URL manualmente.');
      window.prompt('Copia esta URL:', url);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      {/* Main info - always visible */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Status indicator */}
          <div className={`w-14 h-14 rounded-xl ${status.bgColor} flex items-center justify-center shrink-0`}>
            <StatusIcon className={`w-7 h-7 ${status.color}`} />
          </div>
          
          {/* Basic Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold">{camera.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs ${status.bgColor} ${status.color}`}>
                {status.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-2">{camera.club_name}</p>
            
            {/* Quick stats */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatLastSeen(camera.last_seen)}
              </span>
              <span className="flex items-center gap-1">
                {camera.is_wifi ? (
                  camera.online ? <Wifi className="w-3 h-3 text-green-400" /> : <WifiOff className="w-3 h-3 text-red-400" />
                ) : (
                  <Server className="w-3 h-3" />
                )}
                {camera.connection_type}
              </span>
              <span className="flex items-center gap-1">
                <Signal className="w-3 h-3" />
                {camera.uptime_percentage?.toFixed(1)}%
              </span>
            </div>
            
            {/* Error message */}
            {camera.error_message && !camera.online && (
              <p className="mt-2 text-xs text-red-400 bg-red-500/10 px-2 py-1.5 rounded">
                ⚠️ {camera.error_message}
              </p>
            )}
          </div>
        </div>
        
        {/* === CLIP TEST SECTION === */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Simulador de Video</span>
            </div>
            {/* Court Assignment Status */}
            <div className="flex items-center gap-2">
              {isAssignedToCourt ? (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                  <CheckCircle2 className="w-3 h-3" />
                  Cancha {camera.court_number || camera.court_id}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                  <AlertTriangle className="w-3 h-3" />
                  Sin cancha asignada
                </span>
              )}
            </div>
          </div>
          
          {/* Camera offline warning */}
          {!camera.online && (
            <div className="mb-3 p-2 rounded-lg bg-red-500/10 text-red-400 text-xs flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Cámara offline - No se pueden generar clips
            </div>
          )}
          
          {/* User assignment input */}
          {camera.online && (
            <div className="mb-3">
              <label className="text-xs text-muted-foreground mb-1 block">
                Asignar clip a usuario (opcional):
              </label>
              <input
                type="text"
                placeholder="Email, teléfono o ID de usuario"
                value={userIdentifier}
                onChange={(e) => setUserIdentifier(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si se especifica, el clip aparecerá en &quot;Mis Replays&quot; de ese usuario
              </p>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-2"
              onClick={() => handleTestClip(30)}
              disabled={testingClip || !camera.online}
            >
              {testingClip === '30' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Clip 30s
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-2"
              onClick={() => handleTestClip(60)}
              disabled={testingClip || !camera.online}
            >
              {testingClip === '60' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <Play className="w-4 h-4" />
              )}
              <span className="flex flex-col items-start leading-tight">
                <span>Clip 60s</span>
                <span className="text-[10px] text-muted-foreground">Rápido (2x)</span>
              </span>
            </Button>
          </div>
          
          {/* Test Result Display */}
          <AnimatePresence>
            {testResult && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 overflow-hidden"
              >
                {testResult.status === 'success' ? (
                  <div className="bg-green-500/10 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-medium text-green-400">¡Video listo!</span>
                    </div>
                    
                    {/* Show who the clip was assigned to */}
                    {testResult.assigned_to_user && (
                      <div className="flex items-center gap-2 text-xs text-green-300 bg-green-500/10 p-2 rounded">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>
                          Clip asignado a: {testResult.assigned_to_user.email || testResult.assigned_to_user.phone}
                          {testResult.assigned_to_user.name && ` (${testResult.assigned_to_user.name})`}
                        </span>
                      </div>
                    )}
                    
                    {testResult.clip_url && (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={testResult.clip_url}
                          className="flex-1 bg-background/50 text-xs px-2 py-1.5 rounded border border-border truncate"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0"
                          onClick={() => copyToClipboard(testResult.clip_url)}
                        >
                          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0"
                          onClick={() => window.open(testResult.clip_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Steps Summary */}
                    <div className="text-xs text-muted-foreground">
                      {testResult.steps?.map((step, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          {step.status === 'passed' ? (
                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                          <span>{step.name}</span>
                          {step.details && <span className="text-muted-foreground"> - {step.details}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-500/10 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Error en la prueba</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {testResult.error || testResult.steps?.find(s => s.status === 'failed')?.error || 'Error desconocido'}
                    </p>
                    {/* Steps Summary */}
                    {testResult.steps?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/50">
                        {testResult.steps?.map((step, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            {step.status === 'passed' ? (
                              <CheckCircle2 className="w-3 h-3 text-green-400" />
                            ) : step.status === 'failed' ? (
                              <XCircle className="w-3 h-3 text-red-400" />
                            ) : (
                              <Clock className="w-3 h-3 text-yellow-400" />
                            )}
                            <span>{step.name}</span>
                            {step.error && <span className="text-red-400"> - {step.error}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Clear result button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-xs"
                  onClick={() => setTestResult(null)}
                >
                  Cerrar resultado
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Expand button */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 gap-2"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Ocultar detalles
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Ver ficha técnica
            </>
          )}
        </Button>
      </div>
      
      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-border bg-secondary/30"
          >
            <div className="p-4 space-y-1">
              {/* Section: Device Info */}
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-primary" />
                Información del Dispositivo
              </h4>
              
              <InfoRow 
                icon={Camera} 
                label="Modelo" 
                value={camera.model}
              />
              <InfoRow 
                icon={Info} 
                label="Número de serie" 
                value={camera.device_serial}
              />
              <InfoRow 
                icon={Cpu} 
                label="Firmware" 
                value={camera.firmware_version}
              />
              <InfoRow 
                icon={Server} 
                label="Categoría" 
                value={camera.device_category === 'encodingDevice' ? 'Dispositivo de grabación' : camera.device_category}
              />
              
              {/* Section: Connection Status */}
              <h4 className="font-medium text-sm mb-3 mt-4 flex items-center gap-2">
                <Wifi className="w-4 h-4 text-primary" />
                Estado de Conexión
              </h4>
              
              <InfoRow 
                icon={camera.online ? CheckCircle2 : XCircle} 
                label="Estado en línea" 
                value={camera.online ? 'Conectado' : 'Desconectado'}
                valueClass={camera.online ? 'text-green-400' : 'text-red-400'}
              />
              <InfoRow 
                icon={Wifi} 
                label="Tipo de conexión" 
                value={camera.connection_type}
              />
              {camera.is_wifi && (
                <InfoRow 
                  icon={Signal} 
                  label="Señal WiFi" 
                  value={camera.wifi_signal}
                  valueClass={camera.wifi_signal === 'Conectado' ? 'text-green-400' : 'text-red-400'}
                />
              )}
              <InfoRow 
                icon={Zap} 
                label="Alimentación" 
                value={camera.online ? 'Encendida' : 'Sin energía o apagada'}
                valueClass={camera.online ? 'text-green-400' : 'text-yellow-400'}
              />
              
              {/* Section: Cloud & API */}
              <h4 className="font-medium text-sm mb-3 mt-4 flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-primary" />
                Almacenamiento y API
              </h4>
              
              <InfoRow 
                icon={HardDrive} 
                label="Almacenamiento cloud" 
                value={camera.cloud_storage_status === 'enabled' ? 'Habilitado' : 'No disponible'}
                valueClass={camera.cloud_storage_status === 'enabled' ? 'text-green-400' : 'text-yellow-400'}
              />
              <InfoRow 
                icon={Server} 
                label="API Hik-Connect" 
                value={camera.api_status === 'connected' ? 'Conectada' : 'Error'}
                valueClass={camera.api_status === 'connected' ? 'text-green-400' : 'text-red-400'}
              />
              <InfoRow 
                icon={Clock} 
                label="Última verificación API" 
                value={formatDate(camera.api_last_check)}
              />
              
              {/* Section: Registration */}
              <h4 className="font-medium text-sm mb-3 mt-4 flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />
                Registro
              </h4>
              
              <InfoRow 
                icon={Clock} 
                label="Fecha de registro" 
                value={camera.registered_at}
              />
              <InfoRow 
                icon={Info} 
                label="ID de cámara" 
                value={camera.hikconnect_id?.substring(0, 16) + '...'}
              />
              <InfoRow 
                icon={Info} 
                label="Canal" 
                value={camera.channel_no}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Stats summary component
const StatsSummary = ({ counts, lastUpdated }) => (
  <div className="space-y-4 mb-6">
    <div className="grid grid-cols-4 gap-2">
      {[
        { key: 'online', label: 'En línea', color: 'text-green-400 bg-green-500/10' },
        { key: 'offline', label: 'Offline', color: 'text-red-400 bg-red-500/10' },
        { key: 'maintenance', label: 'Mant.', color: 'text-yellow-400 bg-yellow-500/10' },
        { key: 'error', label: 'Error', color: 'text-red-400 bg-red-500/10' },
      ].map(({ key, label, color }) => (
        <div key={key} className={`rounded-xl p-3 text-center ${color}`}>
          <p className="text-2xl font-bold">{counts[key] || 0}</p>
          <p className="text-xs opacity-80">{label}</p>
        </div>
      ))}
    </div>
    {lastUpdated && (
      <p className="text-xs text-muted-foreground text-center">
        Última actualización: {new Date(lastUpdated).toLocaleTimeString('es-MX')}
      </p>
    )}
  </div>
);

export default function AdminCameras() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Extract unique clubs from cameras
  const uniqueClubs = useMemo(() => {
    const clubs = new Map();
    cameras.forEach(c => {
      if (c.club_name && c.club_name !== 'Sin asignar') {
        clubs.set(c.club_name, c.club_id);
      }
    });
    return Array.from(clubs.entries()).map(([name, id]) => ({ name, id })).sort((a, b) => a.name.localeCompare(b.name));
  }, [cameras]);
  
  // Filter cameras based on all filters
  const filteredCameras = useMemo(() => {
    return cameras.filter(camera => {
      // Status filter
      if (statusFilter && camera.status !== statusFilter) {
        return false;
      }
      // Club filter
      if (clubFilter !== 'all' && camera.club_name !== clubFilter) {
        return false;
      }
      return true;
    });
  }, [cameras, statusFilter, clubFilter]);
  
  // Check if any filter is active
  const hasActiveFilters = statusFilter || clubFilter !== 'all';
  
  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setClubFilter('all');
  };
  
  useEffect(() => {
    fetchCameras();
  }, []);
  
  const fetchCameras = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCameras();
      setCameras(response.data.cameras || []);
      setStatusCounts(response.data.status_counts || {});
      setLastUpdated(response.data.last_updated);
    } catch (err) {
      console.error('Cameras fetch error:', err);
      toast.error('Error cargando cámaras');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCameras();
    setRefreshing(false);
    toast.success('Estado actualizado desde Hik-Connect');
  };
  
  return (
    <div className="min-h-screen bg-background overflow-x-hidden admin-mobile">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 max-w-6xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-lg">Estado de Cámaras</h1>
            <p className="text-xs text-muted-foreground">{cameras.length} cámara(s) conectada(s) a Hik-Connect</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-4 overflow-x-hidden">
        {/* Stats summary */}
        <StatsSummary counts={statusCounts} lastUpdated={lastUpdated} />
        
        {/* Info banner */}
        <div className="glass rounded-xl p-3 mb-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
            <Server className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Datos en tiempo real</p>
            <p className="text-xs text-muted-foreground">
              La información se obtiene directamente de la API de Hik-Connect Teams
            </p>
          </div>
        </div>
        
        {/* Filter tabs */}
        <div className="space-y-3 mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { value: '', label: 'Todas' },
              { value: 'online', label: 'En línea' },
              { value: 'offline', label: 'Offline' },
              { value: 'maintenance', label: 'Mantenimiento' },
            ].map(({ value, label }) => (
              <Button
                key={value}
                variant={statusFilter === value ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setStatusFilter(value)}
                className="shrink-0"
              >
                {label}
              </Button>
            ))}
            
            <div className="ml-auto shrink-0">
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
              >
                <Filter className="w-4 h-4" />
                Filtros
              </Button>
            </div>
          </div>
          
          {/* Location Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-secondary/30 rounded-xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Filtros de ubicación
                    </h4>
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        <X className="w-4 h-4 mr-1" />
                        Limpiar
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        Club
                      </label>
                      <Select value={clubFilter} onValueChange={setClubFilter}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Todos los clubes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los clubes</SelectItem>
                          {uniqueClubs.map(club => (
                            <SelectItem key={club.name} value={club.name}>{club.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2">
              {statusFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 rounded-full text-xs">
                  {statusFilter === 'online' ? 'En línea' : statusFilter === 'offline' ? 'Offline' : 'Mantenimiento'}
                  <button onClick={() => setStatusFilter('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {clubFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 rounded-full text-xs">
                  <Building2 className="w-3 h-3" />
                  {clubFilter}
                  <button onClick={() => setClubFilter('all')}><X className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}
          
          {/* Results count */}
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground">
              Mostrando {filteredCameras.length} de {cameras.length} cámaras
            </p>
          )}
        </div>
        
        {/* Cameras grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            Consultando Hik-Connect...
          </div>
        ) : filteredCameras.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
            {hasActiveFilters ? (
              <>
                <p>No se encontraron cámaras con los filtros seleccionados</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              </>
            ) : (
              <>
                <p>No se encontraron cámaras</p>
                <p className="text-sm mt-1">Verifica tu conexión con Hik-Connect en Configuración</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {filteredCameras.map((camera) => (
              <CameraCard key={camera.id} camera={camera} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
